var TorsoView = require('backbone-torso/modules/View');
var accordionWidgetTemplate = require('./accordionWidgetTemplate.hbs');
var DefaultAccordionTriggerView = require('./DefaultAccordionTriggerView');
var DefaultAccordionContentView = require('./DefaultAccordionContentView');

var viewUtils = require('./../../utils/viewUtils');

/**
 * View for the default accordion widget
 * For documentation, go to http://codedocs.qa.vecna.net/ps-frontend/design-demo/design-demo.html#widgets/accordion
 *
 * @class AccordionWidget
 */
var AccordionWidget = TorsoView.extend({
  className: 'brec-accordion',
  template: accordionWidgetTemplate,
  /**
   * Adds attributes to the the top level element (this is to support existing behavioral tests that query these values).
   * @returns {{data-test-accordion: true, data-test-<accordionId>: true}}
   */
  attributes: function() {
    var attributes = {
      'data-test-accordion': true
    };
    var accordionId = this.get('accordionId');
    if (accordionId) {
      var testAttribute = 'data-test-' + accordionId;
      attributes[testAttribute] = true;
    }
    return attributes;
  },

  initialShowAccordionContent: false,

  events: {
    'click [data-accordion]': '_toggleAccordion',
    'keyup [data-accordion]': '_keyboardSupport'
  },

  injectionSites: {
    'accordion-trigger': '_accordionTriggerView',
    'accordion-content': '_accordionContentView'
  },

  /**
   * @method constructor
   * @param [options] {Object}
   *   @param [options.accordionId=this.accordionId] {String} the unique id to use for the accordion.  Required.
   *   @param [options.asListItem=false] {Boolean} true if this accordion is actually an item in a list.
   *   @param [options.contentNoPadding=false] {Boolean} whether to include padding around the content (false) or not include it (true).
   *   @param [options.showAccordionContent=false] {Boolean} true if the accordion content should be shown by default.
   *   @param [options.accordionTriggerViewInstance {TorsoView} an already instantiated view to use for the trigger view.
   *   @param [options.accordionTriggerView {TorsoView.prototype} a prototype of a view that will be instantiated
   *                                                              by this view for the trigger view.
   *   @param [options.accordionTriggerTemplate] {Handlebars.template} this template will be rendered for the trigger content.
   *   @param [options.accordionTriggerMessageKey] {String} this i18n string will be used as the content for the trigger.
   *   @param [options.accordionContentViewInstance {TorsoView} an already instantiated view to use for the content view.
   *   @param [options.accordionContentView {TorsoView.prototype} a prototype of a view that will be instantiated
   *                                                              by this view for the content view.
   *   @param [options.accordionTriggerTemplate] {Handlebars.template} this template will be rendered for the content content.
   *   @param [options.accordionTriggerMessageKey] {String} this i18n string will be used as the content for the content.
   * @override
   */
  constructor: function(options) {
    options = options || {};
    TorsoView.apply(this, arguments);

    this.set('showAccordionContent', options.showAccordionContent || this.initialShowAccordionContent);
    this.on('change:showAccordionContent', this.render);

    var accordionId = options.accordionId || this.accordionId;
    if (accordionId) {
      this.set('accordionId', accordionId);
    } else {
      throw 'accordionId must be provided when instantiating an accordion.';
    }

    this.set('asListItem', options.asListItem || this.asListItem);
    this.set('contentNoPadding', options.contentNoPadding || this.contentNoPadding);

    if (this._accordionTriggerView || this.noTriggerView) {
      // do nothing - already set.
    } else if (options.accordionTriggerViewInstance) {
      this._accordionTriggerView = options.accordionTriggerViewInstance;
    } else {
      var AccordionTriggerView = options.accordionTriggerView || DefaultAccordionTriggerView;
      var accordionTriggerTemplate = options.accordionTriggerTemplate || this._accordionTriggerTemplate ;
      var accordionTriggerMessageKey = options.accordionTriggerMessageKey || this._accordionTriggerMessageKey;
      this._accordionTriggerView = new AccordionTriggerView({
        accordionTriggerTemplate: accordionTriggerTemplate,
        accordionTriggerMessageKey: accordionTriggerMessageKey
      });
    }

    if (this._accordionContentView || this.noContentView) {
      // do nothing - already set.
    } else if (options.accordionContentViewInstance) {
      this._accordionContentView = options.accordionContentViewInstance;
    } else {
      var AccordionContentView = options.accordionContentView || DefaultAccordionContentView;
      var accordionContentTemplate = this._accordionContentTemplate || options.accordionContentTemplate;
      var accordionContentMessageKey = this._accordionContentMessageKey || options.accordionContentMessageKey;
      this._accordionContentView = new AccordionContentView({
        accordionContentTemplate: accordionContentTemplate,
        accordionContentMessageKey: accordionContentMessageKey
      });
    }

    // re-apply attributes to pick up any changes to accordion id or whatever.
    viewUtils.updateViewElAttributes(this);
  },

  /**
   * Show/Hide Accordion Content
   * @method _toggleAccordion
   * @private
   */
  _toggleAccordion: function() {
    var showAccordionContent = this.get('showAccordionContent');
    var newShowAccordionContent = !showAccordionContent && this._shouldShowContent();
    this.set('showAccordionContent', newShowAccordionContent);
  },

  /**
   * Allows sub-classes to force details to never be shown.
   * @method _shouldShowContent
   * @return {boolean} true by default.
   * @private
   */
  _shouldShowContent: function() {
    return true;
  },

  /**
   * Press Enter Key to trigger the _toggleAccordion function
   * @method _keyboardSupport
   * @private
   */
  _keyboardSupport: function(event) {
    var code = event.keyCode || event.which;
    if (code === 13) {
      this._toggleAccordion();
    }
  }
});

module.exports = AccordionWidget;
